import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import Modal from "Components/Generic/Modal";
import { Filters, Searches } from "Models/shipeezi.table";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";

import { getTeamTableColumns } from "../../../../../../../People/Members/Team/getTeamTableColumns";
import useEquipmentActions from "../../../../../hooks/useEquipmentActions";
import { Content } from "./styles";

export interface AddTeamModalProps {
  isOpened: boolean;
  onClose: () => void;
  teamsIdAdded: Array<number>;
  onConfirmAddTeams: (teams: Array<Team>) => void;
}

export const AddTeamModal: React.FC<AddTeamModalProps> = ({
  isOpened,
  onClose,
  teamsIdAdded,
  onConfirmAddTeams,
}) => {
  const { t: translate } = useTranslation();

  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Array<Team>>([]);

  const handleSelect = (idsSelected: number[]) => {
    const teamsSelected = [...selectedTeams, ...teams].filter((member) =>
      idsSelected.includes(member.id!)
    );

    setSelectedTeams(teamsSelected);
  };

  const [page, setPage] = useState<number>(0);
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { getAllTeamsForEquipment, countTeams } = useEquipmentActions();

  const { data: teamsData, isFetching: isFetchingData } = getAllTeamsForEquipment({
    page,
    pageSize,
    searches,
    filters,
  });
  const { data: teamsCount, isFetching: isFetchingCount } = countTeams(filters, searches);

  const onCloseModal = () => {
    setSelectedTeams([]);

    setFilters(undefined);
    setSearches([]);
    onClose();
  };

  useEffect(() => {
    if (teamsData) {
      setTeams(teamsData);
    }
  }, [teamsData]);

  useEffect(() => {
    if (teamsCount) {
      setTotal(teamsCount);
    }
  }, [teamsCount]);

  const columns = getTeamTableColumns({ translate });

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "isActive",
          options: [
            {
              value: "1",
              label: translate("active"),
            },
            {
              value: "0",
              label: translate("inactive"),
            },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  const onSubmit = () => {
    onConfirmAddTeams(selectedTeams);
    onCloseModal();
  };

  return (
    <div>
      <Modal
        open={isOpened}
        handleClose={onClose}
        title={translate("addATeam")}
        showActionButtons
        actionButtonProps={{
          onSave: onSubmit,
          onCancel: onCloseModal,
          textSave: translate("addTeams"),
        }}
      >
        <Content>
          <ShipeeziNewGrid
            data={teams}
            totalItems={total}
            columns={columns}
            loadingData={isFetchingData || isFetchingCount}
            emptyData={translate("noTeamsToShow")}
            selecteds={teamsIdAdded}
            handleSelect={handleSelect}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            onChangePageSize={setPageSize}
            onNextPage={setCurrentPage}
            onPreviousPage={setCurrentPage}
            onGoToPage={setCurrentPage}
            positionActionsColumn="right"
            generalActions={generalActions}
            showSort={false}
          />
        </Content>
      </Modal>
    </div>
  );
};
